import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { ContasReceberService, IListagemContasReceber } from '../../../shared/services/api/Financeiro/ContasReceber';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';

export interface DialogJuntarContasProps {
  open: boolean;
  dados: IListagemContasReceber[];
  vselecionado: number;
  onClose: (value?: string) => void;
}

// Junta as parcelas marcadas na listagem numa única conta consolidada, com
// vencimento novo (renegociação). Se uma das marcadas já for uma conta
// consolidada, o backend soma as demais nela em vez de criar uma nova (ver
// ConsolidacaoReceber.ts) — esse diálogo não precisa saber a diferença, só
// manda a lista de idrec selecionados.
export const DialogJuntarContas: React.FC<DialogJuntarContasProps> = ({
  open, dados, vselecionado, onClose,
}) => {
  const [vencimento, setVencimento] = useState(dayjs().format('YYYY-MM-DD'));
  const [juntando, setJuntando] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  useEffect(() => {
    if (!open) return;
    setVencimento(dayjs().format('YYYY-MM-DD'));
  }, [open]);

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
    if (tms === 'success') onClose('true');
  };

  const confirmar = () => {
    if (dados.length < 2) {
      setTms('warning');
      setMsg('Selecione ao menos duas contas para juntar');
      setOpenmsg(true);
      return;
    }
    if (!vencimento) {
      setTms('warning');
      setMsg('Informe o novo vencimento');
      setOpenmsg(true);
      return;
    }

    setJuntando(true);
    ContasReceberService
      .Juntar(dados.map((conta) => conta.idrec), dayjs(vencimento, 'YYYY-MM-DD').toDate())
      .then((resultado) => {
        setJuntando(false);
        if (resultado instanceof Error) {
          setTms('error');
          setMsg(resultado.message);
          setOpenmsg(true);
          return;
        }
        setTms('success');
        setMsg(`Contas juntadas na conta Nº ${resultado.numero} com sucesso!`);
        setOpenmsg(true);
      });
  };

  const handleCancel = () => onClose('false');

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Juntar contas</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={2500} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Contas selecionadas
        </Typography>
        <TableContainer sx={{ maxHeight: 260, mb: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dados.map((conta) => (
                <TableRow key={conta.idrec}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{conta.numero}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(conta.vreceber))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Total</Typography>
          <Typography variant="h6" fontWeight={700}>{formatarMoeda(vselecionado)}</Typography>
        </Box>

        <TextField
          fullWidth
          size="small"
          type="date"
          label="Novo vencimento"
          InputLabelProps={{ shrink: true }}
          value={vencimento}
          onChange={(e) => setVencimento(e.target.value)}
          disabled={juntando}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleCancel} disabled={juntando}>Cancelar</Button>
        <Button variant="outlined" autoFocus disabled={juntando} onClick={confirmar}>
          {juntando ? 'Juntando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
